import { TFunction } from 'i18next';
import { string, object, number } from 'yup';

// Define el esquema de validación
const validateProfessionalSellerPersonalDataSchema = (tProf: TFunction) =>
  object().shape({
    primeBusinessContactFirstname: string().required(
      tProf('validation.primary_contact_first_name_is_required')
    ),
    primeBusinessContactSecondname: string().notRequired(),
    primeBusinessContactLastname: string().required(
      tProf('validation.primary_contact_last_name_is_required')
    ),
    primeBusinessContactCountry: string().required(
      tProf('validation.primary_contact_country_is_required')
    ),
    primeBusinessContactCitizenship: string().required(
      tProf('validation.primary_contact_citizenship_is_required')
    ),
    primeBusinessContactAddress: string().required(
      tProf('validation.primary_contact_address_is_required')
    ),
    primeBusinessContactCity: string().required(
      tProf('validation.primary_contact_city_is_required')
    ),
    primeBusinessContactDob: string()
      .required(tProf('validation.primary_contact_dob_is_required'))
      .test('is-valid-age', tProf('validation.primary_contact_dob_must_be_valid'), value => {
        if (!value) return false;

        const birthDate = new Date(value);
        const today = new Date();

        // Calculate the age
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDifference = today.getMonth() - birthDate.getMonth();
        const dayDifference = today.getDate() - birthDate.getDate();

        // Adjust the age if the birthday hasn't happened yet
        const adjustedAge =
          monthDifference > 0 || (monthDifference === 0 && dayDifference >= 0) ? age : age - 1;

        // Validate the age
        return adjustedAge >= 18 && adjustedAge < 150;
      }),
  });

const validateProfessionalSellerBusinessDataSchema = (
  isSoleProprietor: boolean,
  tProf: TFunction
) => {
  if (isSoleProprietor) {
    return object().shape({
      businessType: string().required(tProf('validation.business_type_is_required')),
      businessName: string().required(tProf('validation.business_name_is_required')),
      businessNumber: string().notRequired(),
      businessCountry: string().required(tProf('validation.business_country_is_required')),
      businessAddress: string().required(tProf('validation.business_address_is_required')),
      businessCity: string().required(tProf('validation.business_city_is_required')),
      businessPostalCode: string().required(tProf('validation.business_postal_code_is_required')),
      businessTaxId: string().notRequired(),
    });
  }

  return object().shape({
    businessType: string().required(tProf('validation.business_type_is_required')),
    businessName: string().required(tProf('validation.business_name_is_required')),
    businessNumber: string().required(tProf('validation.business_number_is_required')),
    businessCountry: string().required(tProf('validation.business_country_is_required')),
    businessAddress: string().required(tProf('validation.business_address_is_required')),
    businessCity: string().required(tProf('validation.business_city_is_required')),
    businessPostalCode: string().required(tProf('validation.business_postal_code_is_required')),
    businessTaxId: string().required(tProf('validation.business_tax_id_is_required')),
  });
};

const validateProfessionalSellerShareholderSchema = (tProf: TFunction) =>
  object().shape({
    firstName: string().required(tProf('validation.shareholder_first_name_is_required')),
    middleName: string().notRequired(),
    lastName: string().required(tProf('validation.shareholder_last_name_is_required')),
    citizenship: string().required(tProf('validation.shareholder_citizenship_is_required')),
    countryCode: string().required(tProf('validation.shareholder_country_code_is_required')),
    city: string().required(tProf('validation.shareholder_city_is_required')),
    line1: string().required(tProf('validation.shareholder_address_is_required')),
    postalCode: string().required(tProf('validation.shareholder_postal_code_is_required')),
    percentageOfShares: number()
      .min(1, tProf('validation.shareholder_percentage_of_shares_is_invalid'))
      .max(100, tProf('validation.shareholder_percentage_of_shares_is_invalid'))
      .required(tProf('validation.shareholder_percentage_of_shares_is_required')),
  });

export {
  validateProfessionalSellerPersonalDataSchema,
  validateProfessionalSellerBusinessDataSchema,
  validateProfessionalSellerShareholderSchema,
};
